import React, { useState } from "react";
import {
  Badge,
  Box,
  TextField,
  Toolbar,
  Typography,
  Checkbox,
  Divider,
  FormControlLabel,
  MenuItem,
  FormControl,
  InputLabel,
  Popover,
  Select,
  Stack,
  Button,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import FilterListOffIcon from "@mui/icons-material/FilterListOff";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import TicketsExportModal from "./TicketsExportModal";
import { normalizeLicensePlate } from "../../utils/normalizeLicensePlate";
import { ParkingLocation } from "../../types/parkingLocation";
import { useCANparkRoleContext } from "../../context/CANparkRoleProvider";

export interface TicketFilter {
  ticketNumber: string;
  licensePlate: string;
  showLog: boolean;
  showLogAndWarn: boolean;
  showTow: boolean;
  startDate?: string;
  endDate?: string;
  items_per_page: number;
  page: number;
}

const TicketsTableFilterBar: React.FC<{
  filter: TicketFilter;
  setFilter: React.Dispatch<React.SetStateAction<TicketFilter>>;
  parkingLocation: ParkingLocation;
  compact?: boolean;
}> = ({ filter, setFilter, parkingLocation, compact = false }) => {
  const { isLimited } = useCANparkRoleContext();
  const [searchBy, setSearchBy] = useState<"licensePlate" | "ticketNumber">(
    "licensePlate"
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const popoverOpen = Boolean(anchorEl);
  const hasDateFilter = !!(filter.startDate || filter.endDate);
  const hasAnyFilter =
    filter.ticketNumber !== "" ||
    filter.licensePlate !== "" ||
    !filter.showLog ||
    !filter.showLogAndWarn ||
    !filter.showTow ||
    hasDateFilter;

  const clearAllFilters = () => {
    setFilter((prev) => ({
      ...prev,
      ticketNumber: "",
      licensePlate: "",
      showLog: true,
      showLogAndWarn: true,
      showTow: true,
      startDate: undefined,
      endDate: undefined,
    }));
  };

  const startDateValue: Dayjs | null = filter.startDate
    ? dayjs(filter.startDate)
    : null;
  const endDateValue: Dayjs | null = filter.endDate
    ? dayjs(filter.endDate)
    : null;

  const setToday = () => {
    const now = dayjs();
    setFilter((prev) => ({
      ...prev,
      startDate: now.startOf("day").toISOString(),
      endDate: now.endOf("day").toISOString(),
    }));
  };

  const clearDateRange = () => {
    setFilter((prev) => ({
      ...prev,
      startDate: undefined,
      endDate: undefined,
    }));
  };

  // Export modal state
  const [exportOpen, setExportOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const compactMode = compact || isMobile;
  const filtersIconOnly = compactMode && isMobile;
  const showExport = !(compactMode && isMobile);

  const hasSearchValue = !!filter.licensePlate || !!filter.ticketNumber;
  const [searchExpanded, setSearchExpanded] = useState<boolean>(
    !compactMode || hasSearchValue
  );

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const collapseSearch = () => {
    setSearchExpanded(false);
  };

  const filterPopover = (
    <Popover
      anchorEl={anchorEl}
      open={popoverOpen}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <Box sx={{ p: 2, width: 320, maxWidth: "calc(100vw - 32px)" }}>
        <Stack spacing={0.5}>
          <FormControlLabel
            control={
              <Checkbox
                checked={filter.showLog}
                color="secondary"
                onChange={() =>
                  setFilter((prev) => ({ ...prev, showLog: !prev.showLog }))
                }
              />
            }
            label={<Typography variant="body2">Show Logs</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filter.showLogAndWarn}
                color="warning"
                onChange={() =>
                  setFilter((prev) => ({
                    ...prev,
                    showLogAndWarn: !prev.showLogAndWarn,
                  }))
                }
              />
            }
            label={<Typography variant="body2">Show Warnings</Typography>}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={filter.showTow}
                color="error"
                onChange={() =>
                  setFilter((prev) => ({ ...prev, showTow: !prev.showTow }))
                }
              />
            }
            label={<Typography variant="body2">Show Tows</Typography>}
          />
        </Stack>

        <Divider sx={{ my: 1.5 }} />

        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: "0.08em" }}
        >
          Date range
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
            <DatePicker
              label="Start"
              value={startDateValue}
              onChange={(newValue: Dayjs | null) =>
                setFilter((prev) => ({
                  ...prev,
                  startDate: newValue
                    ? newValue.startOf("day").toISOString()
                    : undefined,
                }))
              }
              inputFormat="DD/MM/YYYY"
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
            <DatePicker
              label="End"
              value={endDateValue}
              onChange={(newValue: Dayjs | null) =>
                setFilter((prev) => ({
                  ...prev,
                  endDate: newValue
                    ? newValue.endOf("day").toISOString()
                    : undefined,
                }))
              }
              inputFormat="DD/MM/YYYY"
              renderInput={(params) => (
                <TextField {...params} size="small" fullWidth />
              )}
            />
          </Stack>
        </LocalizationProvider>
        <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
          <Button size="small" variant="outlined" onClick={setToday}>
            Today
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={clearDateRange}
            disabled={!hasDateFilter}
          >
            Show all
          </Button>
        </Stack>
      </Box>
    </Popover>
  );

  const filtersButtonInner = filtersIconOnly ? (
    <Tooltip title="Filters" arrow>
      <Button
        onClick={handlePopoverOpen}
        variant="outlined"
        color="primary"
        sx={{ minWidth: 0, px: 1 }}
        aria-label="Filters"
      >
        <FilterListIcon fontSize="small" />
      </Button>
    </Tooltip>
  ) : (
    <Button
      onClick={handlePopoverOpen}
      variant="outlined"
      color="primary"
      startIcon={<FilterListIcon />}
    >
      Filters
    </Button>
  );

  const filtersButton = (
    <Badge
      color="primary"
      variant="dot"
      invisible={!hasDateFilter}
      overlap="rectangular"
    >
      {filtersButtonInner}
    </Badge>
  );

  const exportButton = (
    <Tooltip title="Download Record Data" arrow>
      <span>
        <Button
          variant="outlined"
          size="medium"
          color="warning"
          sx={{ ml: 1, minWidth: 0, px: 1 }}
          onClick={() => setExportOpen(true)}
          aria-label="Export records"
        >
          <DownloadIcon fontSize="small" />
        </Button>
      </span>
    </Tooltip>
  );

  const searchControls = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        width: { xs: "100%", sm: "auto" },
      }}
    >
      <FormControl
        variant="outlined"
        size="small"
        sx={{ minWidth: 130, flexShrink: 0 }}
      >
        <InputLabel shrink>Search By</InputLabel>
        <Select
          value={searchBy}
          label="Search By"
          onChange={(e) => {
            if (e.target.value === "licensePlate") {
              setFilter((prev) => ({
                ...prev,
                ticketNumber: "",
              }));
            } else {
              setFilter((prev) => ({
                ...prev,
                licensePlate: "",
              }));
            }
            setSearchBy(e.target.value as "licensePlate" | "ticketNumber");
          }}
          sx={{
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            "& fieldset": {
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
        >
          <MenuItem value="licensePlate">License Plate</MenuItem>
          <MenuItem value="ticketNumber">Record Number</MenuItem>
        </Select>
      </FormControl>

      <TextField
        autoFocus={compactMode}
        value={
          searchBy === "licensePlate"
            ? filter.licensePlate
            : filter.ticketNumber
        }
        label={
          searchBy === "licensePlate"
            ? "Search License Plate"
            : "Search Record #"
        }
        variant="outlined"
        size="small"
        sx={{
          flex: 1,
          minWidth: 120,
          maxWidth: { xs: "none", sm: 250 },
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          "& fieldset": {
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
          },
        }}
        onChange={(e) => {
          if (searchBy === "licensePlate") {
            setFilter((prev) => ({
              ...prev,
              licensePlate: normalizeLicensePlate(e.target.value),
            }));
          } else {
            setFilter((prev) => ({
              ...prev,
              ticketNumber: e.target.value,
            }));
          }
        }}
      />
      {compact && (
        <Tooltip title="Hide search" arrow>
          <IconButton
            size="small"
            onClick={collapseSearch}
            aria-label="Hide search"
            sx={{ ml: 0.5 }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {!compact && exportButton}
    </Box>
  );

  const searchToggle = (
    <Tooltip
      title={hasSearchValue ? "Edit search" : "Search"}
      arrow
    >
      <Button
        onClick={() => setSearchExpanded(true)}
        variant={hasSearchValue ? "contained" : "outlined"}
        color="primary"
        startIcon={<SearchIcon />}
        size={isMobile ? "small" : "medium"}
        sx={{ whiteSpace: "nowrap", maxWidth: 200 }}
        aria-label="Search records"
      >
        {hasSearchValue
          ? filter.licensePlate || filter.ticketNumber
          : "Search"}
      </Button>
    </Tooltip>
  );

  return (
    <Toolbar
      sx={{
        width: "100%",
        flexWrap: "wrap",
        rowGap: 1,
        py: { xs: 1, sm: 0 },
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: "100%",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
        }}
      >
        {filtersButton}
        <Tooltip title={hasAnyFilter ? "Clear filters" : "No filters to clear"} arrow>
          <span>
            <IconButton
              onClick={clearAllFilters}
              disabled={!hasAnyFilter}
              color="primary"
              aria-label="Clear filters"
              size="small"
            >
              <FilterListOffIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        {filterPopover}

        {compact && !searchExpanded ? searchToggle : searchControls}

        {compact && showExport && exportButton}
      </Box>
      <TicketsExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        parkingLocation={parkingLocation}
        filter={filter}
      />
    </Toolbar>
  );
};

export default TicketsTableFilterBar;
